/** @jsx jsx */
import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';
import { withAnalyticsContext, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { LinkButton } from '@atlaskit/button/new';
import { css } from '@atlaskit/css';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import EditIcon from '@atlaskit/icon/core/edit';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { getRequestTypeSettingsUrl } from '@atlassian/help-center-common-util/url/url';
import messages from '../messages';

interface EditRequestTypeHoverButtonProps {
	projectKey: string;
	requestTypeId: number;
	ariaLabel?: string;
}

const EditRequestTypeHoverButton = (props: EditRequestTypeHoverButtonProps) => {
	const { formatMessage } = useIntl();
	const { projectKey, requestTypeId, ariaLabel } = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClickButton = (e: React.MouseEvent | React.KeyboardEvent) => {
		e.stopPropagation();
		createAnalyticsEvent({
			actionSubjectId: 'EditRequestTypeHoverButton',
			action: 'clicked',
			actionSubject: 'button',
			analyticsType: UI_EVENT_TYPE,
		}).fire();
	};
	const defaultLabel = formatMessage(messages.editRequestTypeLabel);
	const label = ariaLabel ? `${defaultLabel} - ${ariaLabel}` : defaultLabel;

	return (
		<div css={ButtonStyles}>
			<LinkButton
				href={getRequestTypeSettingsUrl(projectKey, requestTypeId)}
				appearance="subtle"
				target="_blank"
				testId="edit-request-type-hover-button"
				onClick={onClickButton}
				spacing="compact"
				shouldFitContainer
				iconBefore={EditRequestTypeHoverButtonEditIcon}
				aria-label={label}
			>
				<Text weight="medium" color="color.text.subtle">
					{defaultLabel}
				</Text>
			</LinkButton>
		</div>
	);
};

const EditRequestTypeHoverButtonEditIcon = (iconProps: NewCoreIconProps) => {
	const { formatMessage } = useIntl();
	return (
		<EditIcon
			{...iconProps}
			label={formatMessage(messages.managePortalGroupsLabel)}
			spacing="none"
			color={token('color.icon', '#505258')}
		/>
	);
};

const ButtonStyles = css({
	borderStyle: 'solid',
	borderRadius: token('radius.small', '4px'),
	borderColor: token('color.border', '#0B122824'),
	borderWidth: token('border.width', '1px'),
	boxSizing: 'border-box',
});

export default withAnalyticsContext({ component: 'EditRequestTypeHoverButton' })(
	EditRequestTypeHoverButton,
);
